import React, { useState, useEffect } from 'react';
import bulletinService from '../../services/bulletinService';
import contentService from '../../services/contentService';
import { useSiteSettings } from '../../context/SiteSettingsContext';

const BulletinForm = ({ compact = false, variant = 'banner' }) => {
  const isPopup = variant === 'popup';
  const { eBulletinRecaptchaActive, eBulletinRecaptchaSiteKey } = useSiteSettings();
  const [email, setEmail] = useState('');
  const [kvkk, setKvkk] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [kvkkHtml, setKvkkHtml] = useState(null);
  const [kvkkPdfUrl, setKvkkPdfUrl] = useState(null);

  useEffect(() => {
    contentService.getKvkkText()
      .then((html) => { if (html) setKvkkHtml(html); })
      .catch(() => {});
    contentService.getKvkkPdfUrl()
      .then((url) => { if (url) setKvkkPdfUrl(url); })
      .catch(() => {});
  }, []);

  // reCAPTCHA v3 script'ini dinamik olarak yükle
  useEffect(() => {
    if (!eBulletinRecaptchaActive || !eBulletinRecaptchaSiteKey) return;
    const scriptId = 'google-recaptcha-v3';
    if (document.getElementById(scriptId)) return;
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = `https://www.google.com/recaptcha/api.js?render=${eBulletinRecaptchaSiteKey}`;
    script.async = true;
    document.head.appendChild(script);
  }, [eBulletinRecaptchaActive, eBulletinRecaptchaSiteKey]);

  const validateEmail = (val) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!validateEmail(email)) {
      setErrorMsg('Geçerli bir e-posta adresi giriniz.');
      return;
    }
    if (!kvkk) {
      setErrorMsg('Devam edebilmek için KVKK metnini onaylamanız gerekmektedir.');
      return;
    }

    setStatus('loading');
    try {
      let recaptchaToken = null;
      if (eBulletinRecaptchaActive && eBulletinRecaptchaSiteKey && window.grecaptcha) {
        recaptchaToken = await new Promise((resolve) => {
          window.grecaptcha.ready(() => {
            window.grecaptcha
              .execute(eBulletinRecaptchaSiteKey, { action: 'subscribe' })
              .then(resolve);
          });
        });
      }
      const data = await bulletinService.subscribe(email, recaptchaToken);
      if (data?.isSuccess === true) {
        setStatus('success');
        setEmail('');
        setKvkk(false);
      } else {
        setStatus('error');
        setErrorMsg(
          data?.errors?.[0] ||
          data?.message ||
          'Abonelik işlemi sırasında bir hata oluştu. Lütfen tekrar deneyiniz.'
        );
      }
    } catch (err) {
      setStatus('error');
      const data = err?.response?.data;
      setErrorMsg(
        data?.errors?.[0] ||
        data?.message ||
        'Abonelik işlemi sırasında bir hata oluştu. Lütfen tekrar deneyiniz.'
      );
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center py-4 text-center">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 ${isPopup ? 'bg-blue-100' : 'bg-white/20'}`}>
          <svg className={`w-6 h-6 ${isPopup ? 'text-blue-600' : 'text-white'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className={`font-semibold text-lg ${isPopup ? 'text-gray-900 dark:text-white' : 'text-white'}`}>
          Abone oldunuz!
        </p>
        <p className={`text-sm mt-1 ${isPopup ? 'text-gray-500 dark:text-gray-400' : 'text-white/80'}`}>
          En güncel haberler e-posta adresinize gönderilecektir.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className={`flex gap-2 ${compact ? 'flex-col sm:flex-row' : 'flex-col sm:flex-row'}`}>
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setStatus('idle'); setErrorMsg(''); }}
          placeholder="E-posta adresinizi giriniz"
          required
          disabled={status === 'loading'}
          className={`flex-1 px-4 py-2.5 rounded-lg border text-sm
            focus:outline-none focus:ring-2 focus:border-transparent disabled:opacity-60
            ${isPopup
              ? 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-blue-500'
              : 'border-white/30 bg-white/10 text-white placeholder-white/60 focus:ring-white/50'
            }`}
        />
        <button
          type="submit"
          disabled={status === 'loading'}
          className={`px-6 py-2.5 font-semibold rounded-lg transition-colors text-sm whitespace-nowrap
            focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60
            ${isPopup
              ? 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500 focus:ring-offset-white'
              : 'bg-white hover:bg-white/90 text-blue-700 focus:ring-white focus:ring-offset-blue-600'
            }`}
        >
          {status === 'loading' ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Gönderiliyor...
            </span>
          ) : 'Abone Ol'}
        </button>
      </div>

      {/* KVKK Onayı */}
      <label className="flex items-start gap-2 mt-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={kvkk}
          onChange={(e) => { setKvkk(e.target.checked); setErrorMsg(''); }}
          disabled={status === 'loading'}
          className={`mt-0.5 w-4 h-4 rounded text-blue-600 disabled:opacity-60 flex-shrink-0
            ${isPopup ? 'border-gray-300 focus:ring-blue-500' : 'border-white/40 focus:ring-white'}`}
        />
        {kvkkHtml ? (
          <span
            className={`text-xs leading-relaxed ${isPopup ? 'text-gray-600 dark:text-gray-300 [&_a]:text-blue-600 [&_a]:underline [&_a]:font-medium' : 'text-white/80 [&_a]:text-white [&_a]:underline [&_a]:font-medium'}`}
            dangerouslySetInnerHTML={{
              __html: kvkkPdfUrl
                ? kvkkHtml.replace(/href="[^"]*"/, `href="${kvkkPdfUrl}"`)
                : kvkkHtml
            }}
          />
        ) : (
          <span className={`text-xs leading-relaxed ${isPopup ? 'text-gray-600 dark:text-gray-300' : 'text-white/80'}`}>
            <a
              href={kvkkPdfUrl || '#'}
              target="_blank"
              rel="noreferrer"
              className={`font-medium underline ${isPopup ? 'text-blue-600' : 'text-white'}`}
            >
              KVKK Aydınlatma Metni
            </a>'ni
            okudum ve kişisel verilerimin işlenmesine onay veriyorum.
          </span>
        )}
      </label>

      {errorMsg && (
        <p className={`mt-2 text-xs font-medium ${isPopup ? 'text-red-600 dark:text-red-400' : 'text-yellow-300'}`}>{errorMsg}</p>
      )}
    </form>
  );
};

export default BulletinForm;
